import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useLang } from "../context/LangContext";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger
} from "../components/ui/dialog";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import { toast } from "sonner";
import { MoreVertical, ShieldCheck, UserCog, Ban, KeyRound, Trash2, UserCheck } from "lucide-react";

export default function UserManagement() {
  const { t, lang } = useLang();
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [resetUser, setResetUser] = useState(null);
  const [resetPw, setResetPw] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => { load(); }, []);

  const approve = async (u, role) => {
    try {
      await api.post(`/users/${u.id}/approve?role=${role}`);
      toast.success(t("common.saved"));
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const setRole = async (u, role) => {
    try {
      await api.patch(`/users/${u.id}`, { role });
      toast.success(t("common.saved"));
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const setStatus = async (u, status) => {
    try {
      await api.patch(`/users/${u.id}`, { status });
      toast.success(t("common.saved"));
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (u) => {
    if (!window.confirm(t("users.confirmDelete"))) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success(t("common.saved"));
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const submitReset = async () => {
    if (!resetPw || resetPw.length < 6) {
      toast.error("min. 6 Zeichen");
      return;
    }
    try {
      await api.post(`/users/${resetUser.id}/reset-password`, { new_password: resetPw });
      toast.success(t("common.saved"));
      setResetUser(null);
      setResetPw("");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const statusBadgeClass = (s) =>
    s === "active" ? "status-abgeschlossen" : s === "pending" ? "status-in-bearbeitung" : "status-storniert";

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
      <p className="label-mini">Admin</p>
      <h1 className="text-3xl sm:text-4xl font-bold tracking-tight leading-none mt-1 mb-6">{t("users.title")}</h1>

      <div className="swiss-card overflow-hidden">
        <div className="overflow-x-auto table-scroll">
          <table className="w-full text-sm" data-testid="users-table">
            <thead className="bg-[#F8F9FA] border-b border-border">
              <tr>
                <th className="px-3 py-3 text-left font-semibold">{t("users.cols.name")}</th>
                <th className="px-3 py-3 text-left font-semibold">{t("users.cols.email")}</th>
                <th className="px-3 py-3 text-left font-semibold">{t("users.cols.username")}</th>
                <th className="px-3 py-3 text-left font-semibold">{t("users.cols.role")}</th>
                <th className="px-3 py-3 text-left font-semibold">{t("users.cols.status")}</th>
                <th className="px-3 py-3 text-left font-semibold">{t("users.cols.created")}</th>
                <th className="px-3 py-3 text-right font-semibold">{t("users.cols.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-b border-border last:border-0 hover:bg-[#F8F9FA]" data-testid={`user-row-${u.username}`}>
                  <td className="px-3 py-3 font-medium">{u.name}</td>
                  <td className="px-3 py-3 text-muted-foreground">{u.email}</td>
                  <td className="px-3 py-3 mono">{u.username}</td>
                  <td className="px-3 py-3">
                    <span className="text-xs font-medium px-2 py-1 border border-border" style={{ borderRadius: 4 }}>
                      {t(`users.roles.${u.role}`)}
                    </span>
                  </td>
                  <td className="px-3 py-3">
                    <span className={`${statusBadgeClass(u.status)} text-xs font-medium px-2 py-1 border`} style={{ borderRadius: 4 }} data-testid={`user-status-${u.username}`}>
                      {t(`users.statuses.${u.status}`)}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-muted-foreground whitespace-nowrap text-xs">
                    {u.created_at && new Date(u.created_at).toLocaleDateString(lang === "de" ? "de-DE" : "en-US")}
                  </td>
                  <td className="px-3 py-3 text-right">
                    {me?.id === u.id ? (
                      <span className="text-xs text-muted-foreground">—</span>
                    ) : (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="outline" size="sm" data-testid={`user-actions-${u.username}`} style={{ borderRadius: 4 }}>
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {u.status === "pending" && (
                            <>
                              <DropdownMenuItem onClick={() => approve(u, "employee")} data-testid={`approve-employee-${u.username}`}>
                                <UserCheck className="w-4 h-4 mr-2" /> {t("users.approve")} ({t("users.roles.employee")})
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => approve(u, "admin")} data-testid={`approve-admin-${u.username}`}>
                                <ShieldCheck className="w-4 h-4 mr-2" /> {t("users.approve")} ({t("users.roles.admin")})
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                            </>
                          )}
                          {u.role === "employee" && u.status === "active" && (
                            <DropdownMenuItem onClick={() => setRole(u, "admin")} data-testid={`make-admin-${u.username}`}>
                              <ShieldCheck className="w-4 h-4 mr-2" /> {t("users.makeAdmin")}
                            </DropdownMenuItem>
                          )}
                          {u.role === "admin" && u.status === "active" && (
                            <DropdownMenuItem onClick={() => setRole(u, "employee")} data-testid={`make-employee-${u.username}`}>
                              <UserCog className="w-4 h-4 mr-2" /> {t("users.makeEmployee")}
                            </DropdownMenuItem>
                          )}
                          {u.status === "active" ? (
                            <DropdownMenuItem onClick={() => setStatus(u, "blocked")} data-testid={`block-${u.username}`}>
                              <Ban className="w-4 h-4 mr-2" /> {t("users.block")}
                            </DropdownMenuItem>
                          ) : u.status === "blocked" ? (
                            <DropdownMenuItem onClick={() => setStatus(u, "active")} data-testid={`unblock-${u.username}`}>
                              <UserCheck className="w-4 h-4 mr-2" /> {t("users.unblock")}
                            </DropdownMenuItem>
                          ) : null}
                          <DropdownMenuItem onClick={() => setResetUser(u)} data-testid={`reset-${u.username}`}>
                            <KeyRound className="w-4 h-4 mr-2" /> {t("users.reset")}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => remove(u)} className="text-destructive" data-testid={`delete-${u.username}`}>
                            <Trash2 className="w-4 h-4 mr-2" /> {t("users.delete")}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!resetUser} onOpenChange={(o) => !o && setResetUser(null)}>
        <DialogContent style={{ borderRadius: 4 }} data-testid="reset-password-dialog">
          <DialogHeader>
            <DialogTitle>{t("users.reset")} — {resetUser?.username}</DialogTitle>
          </DialogHeader>
          <Input
            type="password"
            value={resetPw}
            onChange={(e) => setResetPw(e.target.value)}
            placeholder={t("users.newPassword")}
            data-testid="reset-password-input"
            style={{ borderRadius: 4 }}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetUser(null)} style={{ borderRadius: 4 }} data-testid="reset-cancel">{t("form.cancel")}</Button>
            <Button onClick={submitReset} className="bg-[#0055FF] hover:bg-[#0044CC] text-white" style={{ borderRadius: 4 }} data-testid="reset-confirm">{t("profile.save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
